import HttpClient from 'services/HttpClient'

export interface IAiLimitsResponse {
  hasLimit: boolean
  type?: 'user' | 'brand_country' | 'global'
  duration?: 'monthly' | 'one_time'
  monthlyLimit?: number
  currentUsage?: number
  remaining?: number
  resetDate?: string
  autoRenew?: boolean
  isRenewable?: boolean
}

export interface IApplicableLimitResponse {
  hasLimit: boolean
  label?: string // Pre-formatted: "Global limit", "User limit", "Brand (User Default Country) limit"
  type?: 'global' | 'user' | 'brand_country'
  limitValue?: number // Note: different from monthlyLimit in old response
  currentUsage?: number
  remaining?: number
  brandName?: string // Only for brand_country type
  countryCode?: string // Only for brand_country type
  message?: string // Error message when hasLimit: false
}

export interface IAiLimitsParams {
  operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
  userId?: string
  brandId?: string
  countryCode?: string
}

export interface IAiLimitCreate {
  type: 'user' | 'brand_country' | 'global'
  operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
  limitValue: number
  duration?: 'monthly' | 'one_time'
  brandId?: string
  countryCode?: string
  userId?: string
  autoRenew?: boolean
}

export interface IAiLimitUpdate {
  limitValue?: number
  isActive?: boolean
  autoRenew?: boolean
}

export interface IAiLimit {
  id: string
  type: 'user' | 'brand_country' | 'global'
  operationType: 'translation' | 'image_text_generation' | 'asset_image_text_generation'
  duration: 'monthly' | 'one_time'
  brandId?: string
  countryCode?: string
  userId?: string
  limitValue: number
  currentUsage: number
  resetDate?: string
  autoRenew?: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export class AiLimitsRepository {
  api: typeof HttpClient

  constructor(api: typeof HttpClient) {
    this.api = api
  }

  getAiLimits(params: IAiLimitsParams) {
    const queryParams = new URLSearchParams()

    queryParams.append('operationType', params.operationType)

    if (params.userId) {
      queryParams.append('userId', params.userId)
    }

    if (params.brandId) {
      queryParams.append('brandId', params.brandId)
    }

    if (params.countryCode) {
      queryParams.append('countryCode', params.countryCode)
    }

    return this.api.get(`/ai-limits/status?${queryParams.toString()}`)
  }

  // Admin functions
  getAllLimits(params?: {
    userId?: string
    type?: 'user' | 'brand_country' | 'global'
    operationType?:
      | 'translation'
      | 'image_text_generation'
      | 'asset_image_text_generation'
  }) {
    const queryParams = new URLSearchParams()

    if (params?.userId) {
      queryParams.append('userId', params.userId)
    }

    if (params?.type) {
      queryParams.append('type', params.type)
    }

    if (params?.operationType) {
      queryParams.append('operationType', params.operationType)
    }

    const queryString = queryParams.toString()

    return this.api.get(`/ai-limits${queryString ? `?${queryString}` : ''}`)
  }

  createLimit(payload: IAiLimitCreate) {
    return this.api.post('/ai-limits', payload)
  }

  createOneTimeLimit(payload: Omit<IAiLimitCreate, 'duration'>) {
    return this.api.post('/ai-limits/one-time', payload)
  }

  updateLimit(limitId: string, payload: IAiLimitUpdate) {
    return this.api.put(`/ai-limits/${limitId}`, payload)
  }

  resetLimit(limitId: string) {
    return this.api.put(`/ai-limits/${limitId}/reset`)
  }

  deleteLimit(limitId: string) {
    return this.api.delete(`/ai-limits/${limitId}`)
  }
}

export default new AiLimitsRepository(HttpClient)
